import React, { useState, useEffect } from "react";
import { Button } from "@heroui/react";
import { X, Check } from "lucide-react";
import { useWallet } from "@solana/wallet-adapter-react";
import { WalletMultiButton } from "@solana/wallet-adapter-react-ui";
import { useGetAccountInfo } from "@/hooks/sdkDappHooks";
import useModalStore from "@/store/useModalStore";
import { IMAGES } from "@/localConstants/images";
import {
  BridgeWalletModalProps,
  NetworkType,
  WalletConnectionState,
} from "./types";

export const BridgeWalletModal: React.FC<BridgeWalletModalProps> = ({
  isOpen,
  onClose,
  requiredNetworks,
  onAllConnected,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isConnecting, setIsConnecting] = useState(false);

  // Wallet connections
  const { connected: solanaConnected } = useWallet();
  const { address: mvxAddress } = useGetAccountInfo();
  const { setConnectWalletModalOpen } = useModalStore();

  const walletStates: WalletConnectionState = {
    solana: solanaConnected,
    multiversx: !!mvxAddress,
  };

  const allConnected = walletStates.multiversx && walletStates.solana;

  // Update current step based on connection states
  useEffect(() => {
    if (allConnected) {
      if (onAllConnected) {
        setTimeout(() => {
          onAllConnected();
          onClose();
        }, 1000);
      }
    } else if (!walletStates.multiversx) {
      setCurrentStep(0); // Step 1: Connect MultiversX
    } else if (!walletStates.solana) {
      setCurrentStep(1); // Step 2: Connect Solana
    }
  }, [solanaConnected, mvxAddress, allConnected, onAllConnected, onClose]);

  const handleGetStarted = () => {
    setIsConnecting(true);
    setConnectWalletModalOpen(true);
    setTimeout(() => setIsConnecting(false), 500);
  };

  const handleConnectSolana = () => {
    setIsConnecting(true);
    const walletButton = document.querySelector(
      ".wallet-adapter-button"
    ) as HTMLButtonElement;
    if (walletButton) {
      walletButton.click();
    }
    setTimeout(() => setIsConnecting(false), 500);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        {/* Container with gap between header and body */}
        <div className="w-full max-w-md space-y-4">
          {/* Status Header (Detached) - Only show after first step */}
          {currentStep > 0 && (
            <div className="bg-neutral-900 rounded-xl px-6 py-4">
              <div className="flex items-center gap-4 w-full">
                <div className="flex items-center gap-2">
                  <div
                    className={`w-3 h-3 rounded-full ${
                      walletStates.multiversx ? "bg-green-500" : "bg-gray-500"
                    }`}
                  />
                  <span className="text-sm text-gray-300 font-roboto">
                    MultiversX
                  </span>
                </div>
                <div className="flex-1 h-px bg-gradient-to-r from-gray-600 to-gray-600" />
                <div className="flex items-center gap-2">
                  <div
                    className={`w-3 h-3 rounded-full ${
                      walletStates.solana ? "bg-green-500" : "bg-gray-500"
                    }`}
                  />
                  <span className="text-sm text-gray-300 font-roboto">
                    Solana
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Main Modal Body */}
          <div className="bg-[#0f0f0f] rounded-xl relative py-6">
            {currentStep === 0 && (
              <div className="absolute top-6 right-6 z-20">
                <Button
                  isIconOnly
                  variant="ghost"
                  onPress={onClose}
                  className="text-gray-400 hover:text-white"
                  isDisabled={isConnecting}
                >
                  <X size={20} />
                </Button>
              </div>
            )}

            {/* Content */}
            <div className="px-6 ">
              {allConnected ? (
                // All Connected State
                (<div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check size={32} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2 font-roboto">
                    All wallets connected!
                  </h3>
                  <p className="text-gray-400 font-roboto">
                    You can now bridge your A1X tokens
                  </p>
                </div>)
              ) : currentStep === 0 ? (
                // Step 1: Initial screen with overlapped logos
                (<div className="text-center space-y-8 py-8">
                  {/* Overlapped Logos */}
                  <div className="relative flex items-center justify-center">
                    <div className="bg-black rounded-full p-2 z-10">
                      <img
                        src={IMAGES.MULTIVERSX_LOGO}
                        alt="MultiversX"
                        className="w-16 h-16 rounded-full"
                      />
                    </div>
                    <div className="bg-black rounded-full p-2 -ml-8">
                      <img
                        src={IMAGES.SOLANA_LOGO}
                        alt="Solana"
                        className="w-16 h-16 rounded-full"
                      />
                    </div>
                  </div>
                  {/* Title and Description */}
                  <div className="space-y-3">
                    <h2 className="text-2xl font-bold text-white font-roboto">
                      Connect to Bridge
                    </h2>
                    <p className="text-gray-400 font-roboto">
                      You'll need to connect both your MultiversX and Solana
                      wallets to get started
                    </p>
                  </div>
                  {/* Get Started Button */}
                  <Button
                    className="w-full bg-green-600 hover:bg-green-700 text-white border-0 font-roboto"
                    onPress={handleGetStarted}
                    isLoading={isConnecting}
                  >
                    Get started
                  </Button>
                </div>)
              ) : (
                // Step 2: Connect Solana
                (<div className="text-center space-y-6">
                  <img
                    src={IMAGES.SOLANA_LOGO}
                    alt="Solana"
                    className="w-16 h-16 rounded-full mx-auto"
                  />
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2 font-roboto">
                      Nice! Now let's connect your Solana wallet
                    </h3>
                    <p className="text-gray-400 font-roboto">
                      Connect your Solana wallet to complete the setup
                    </p>
                  </div>
                  <Button
                    className="w-full bg-green-600 hover:bg-green-700 text-white border-0 font-roboto h-12"
                    onPress={handleConnectSolana}
                    isLoading={isConnecting}
                  >
                    Connect Solana wallet
                  </Button>
                </div>)
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Hidden Solana Wallet Button */}
      <div className="hidden">
        <WalletMultiButton />
      </div>
    </>
  );
};
